/**
 * 图片滤镜插件控制器
 *
 * 统一管理 MainLayer 与 ImageLayer 的离线滤镜会话、临时画布预览、最终提交、事件和历史。
 */
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import { filterBridge } from '@meitu/wasm-lib';
import { Framework, type IEdgelessPreviewClearable, type IGetSrcImage, type ISetResource } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { convertImageToOrigin, createCanvas } from '@/utils/file-kit';
import { setLayerOutputSourceCanvas } from '@/utils/layer-output-draft-kit';
import { LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';
import { getImageFilterStore } from './state';
import {
    IMAGE_FILTER_HISTORY_TYPE,
    ImageFilterEvent,
    type IImageFilterApplyResult,
    type IImageFilterCancelResult,
    type IImageFilterCommitContext,
    type IImageFilterHostOptions,
    type IImageFilterSelection,
    type IImageFilterUISnapshot,
    type ImageFilterStateListener,
    type ISelectImageFilterOptions,
} from './types';
import { appendAppliedFilterId, cloneFilterCanvas, composeFilterCanvas } from './utils';

interface IFilterSession {
    layerId: string;
    originLayer: ILayerProps;
    originCanvas: HTMLCanvasElement;
    sourceCanvas: HTMLCanvasElement;
    resultCanvas: HTMLCanvasElement | null;
    revision: number;
    host: IImageFilterHostOptions;
}

interface IAlphaResult {
    canvas: HTMLCanvasElement;
    filter: IImageFilterSelection;
    revision: number;
}

type IFilterEdgelessObject = IGetSrcImage &
    ISetResource &
    Partial<IEdgelessPreviewClearable> & {
        update?: (onlyUpdateBaseInfo?: boolean) => void | Promise<void>;
        commitAppliedResource?: () => void;
    };

/** 统一图片滤镜控制器。 */
export class ImageFilterPlugin extends Framework {
    private session: IFilterSession | null = null;
    private readonly alphaResultMap = new Map<string, IAlphaResult>();
    private historyListenerId = 0;
    private lifecycleRevision = 0;

    /**
     * 创建图片滤镜插件实例
     *
     * @param identity - 编辑器实例标识
     * 副作用：监听当前编辑器的滤镜历史切换。
     */
    constructor(identity: string) {
        super(identity);
        this.historyListenerId = this.framework.service.history.onBeforeChange(({ data }) => {
            if (data?.type !== IMAGE_FILTER_HISTORY_TYPE) return;
            const layerId = data.data?.item?.newLayer?.id;
            if (layerId && this.session?.layerId === layerId) this.destroy();
        });
    }

    private get store() {
        return getImageFilterStore(this.identity);
    }

    private setSnapshot(partial: Partial<IImageFilterUISnapshot>) {
        const current = this.store.getState().snapshot;
        const snapshot: IImageFilterUISnapshot = {
            ...current,
            ...partial,
            filter: partial.filter === undefined ? current.filter : partial.filter,
        };
        this.store.getState().setSnapshot(snapshot);
        this.framework.service.events.emit(ImageFilterEvent.STATE_CHANGE, snapshot);
    }

    private emitError(error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        this.setSnapshot({ status: 'error', error: message });
        this.framework.service.events.emit(ImageFilterEvent.ERROR, message);
    }

    private getObject(layerId: string) {
        return this.framework.service.edgelessCanvas.getByLayerId(layerId) as unknown as
            | IFilterEdgelessObject
            | undefined;
    }

    private setPreview(layerId: string, canvas: HTMLCanvasElement) {
        const object = this.getObject(layerId);
        if (!object?.setResource) throw new Error(`Image filter edgeless object is missing: ${layerId}`);
        object.setResource(canvas);
        this.framework.service.edgelessCanvas.requestRenderAll();
    }

    private clearPreview(layerId: string, fallbackCanvas?: HTMLCanvasElement) {
        const object = this.getObject(layerId);
        if (!object) return;
        if (object.clearPreviewResource) {
            object.clearPreviewResource();
        } else if (fallbackCanvas && object.setResource) {
            object.setResource(fallbackCanvas);
        } else {
            void object.update?.(false);
        }
        this.framework.service.edgelessCanvas.requestRenderAll();
    }

    private resetState(layerId = '') {
        this.store.getState().setSnapshot({
            layerId,
            filter: null,
            alpha: 1,
            status: 'idle',
            hasPendingChanges: false,
            isPreviewing: false,
            error: '',
        });
        this.framework.service.events.emit(ImageFilterEvent.STATE_CHANGE, this.store.getState().snapshot);
    }

    /**
     * 打开指定图层的滤镜会话
     *
     * @param layerId - 待处理 MainLayer 或 ImageLayer ID
     * @param host - 可选；由 user-editor 注入的外部提交与回滚事务
     * @returns 会话初始化完成后的 UI 快照
     * 副作用：清理旧预览并读取图层源图 Canvas。
     */
    async open(layerId: string, host: IImageFilterHostOptions = {}) {
        if (this.session) this.destroy();
        const lifecycleRevision = ++this.lifecycleRevision;
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        const object = this.getObject(layerId);
        if (!layer || !object?.getSrcImage) throw new Error(`Image filter target is missing: ${layerId}`);

        this.resetState(layerId);
        this.setSnapshot({ status: 'loading' });
        const revision = 1;
        try {
            const image = await object.getSrcImage();
            const sourceCanvas = createCanvas(image.width, image.height, image).canvas;
            if (this.lifecycleRevision !== lifecycleRevision) return this.getSnapshot();
            this.session = {
                layerId,
                originLayer: cloneDeep(layer),
                originCanvas: sourceCanvas,
                sourceCanvas,
                resultCanvas: null,
                revision,
                host,
            };
            this.setSnapshot({ status: 'idle', error: '' });
        } catch (error) {
            if (this.lifecycleRevision !== lifecycleRevision) return this.getSnapshot();
            this.emitError(error);
            throw error;
        }
        return this.getSnapshot();
    }

    /**
     * 离线生成 100% 滤镜结果并显示默认强度
     *
     * @param options - 滤镜素材和分类信息
     * @returns 滤镜服务返回的默认强度，范围 0-1
     * 副作用：更新 Edgeless 临时资源和插件事件状态。
     */
    async select(options: ISelectImageFilterOptions): Promise<number> {
        const session = this.session;
        if (!session) throw new Error('Image filter session is not open');
        const revision = ++session.revision;
        const filter = { filterId: String(options.filterId), categoryId: String(options.categoryId) };
        const previousResultCanvas = session.resultCanvas;
        const previousAlphaResult = this.alphaResultMap.get(session.layerId);
        const previousSnapshot = this.getSnapshot();
        this.setSnapshot({ filter, status: 'loading', error: '' });
        try {
            const defaultAlpha = Math.min(
                1,
                Math.max(0, await filterBridge.getDefaultAlpha(filter.filterId, options.filterUrl)),
            );
            if (this.session !== session || session.revision !== revision) return defaultAlpha;
            const sourceCanvas = cloneFilterCanvas(session.sourceCanvas);
            const { canvas } = await filterBridge.from(sourceCanvas).apply(filter.filterId, options.filterUrl, 1);
            if (!canvas || this.session !== session || session.revision !== revision) return defaultAlpha;
            session.resultCanvas = canvas;
            const previewCanvas = composeFilterCanvas(session.sourceCanvas, canvas, defaultAlpha);
            this.alphaResultMap.set(session.layerId, { canvas: previewCanvas, filter, revision });
            this.setPreview(session.layerId, previewCanvas);
            this.setSnapshot({
                filter,
                alpha: defaultAlpha,
                status: 'ready',
                hasPendingChanges: true,
                isPreviewing: true,
                error: '',
            });
            return defaultAlpha;
        } catch (error) {
            if (this.session === session && session.revision === revision) {
                if (previousResultCanvas && previousAlphaResult && previousSnapshot.filter) {
                    const message = error instanceof Error ? error.message : String(error);
                    session.resultCanvas = previousResultCanvas;
                    this.alphaResultMap.set(session.layerId, {
                        ...previousAlphaResult,
                        revision,
                    });
                    this.setSnapshot({
                        filter: previousSnapshot.filter,
                        alpha: previousSnapshot.alpha,
                        status: 'ready',
                        hasPendingChanges: previousSnapshot.hasPendingChanges,
                        isPreviewing: previousSnapshot.isPreviewing,
                        error: message,
                    });
                    this.framework.service.events.emit(ImageFilterEvent.ERROR, message);
                } else {
                    session.resultCanvas = null;
                    this.alphaResultMap.delete(session.layerId);
                    this.setSnapshot({
                        filter: null,
                        alpha: 1,
                        hasPendingChanges: false,
                        isPreviewing: false,
                    });
                    this.emitError(error);
                }
            }
            throw error;
        }
    }

    /**
     * 更新当前滤镜强度预览
     *
     * @param alpha - 滤镜强度，范围 0-1
     * 副作用：更新私有 alphaResult Map、Edgeless 临时资源和状态事件。
     */
    setAlpha(alpha: number) {
        const session = this.session;
        const snapshot = this.getSnapshot();
        const filter = snapshot.filter;
        if (!session?.resultCanvas || !filter || snapshot.status !== 'ready') return;
        const normalizedAlpha = Math.min(1, Math.max(0, alpha));
        const canvas = composeFilterCanvas(session.sourceCanvas, session.resultCanvas, normalizedAlpha);
        this.alphaResultMap.set(session.layerId, { canvas, filter, revision: session.revision });
        this.setPreview(session.layerId, canvas);
        this.setSnapshot({
            alpha: normalizedAlpha,
            status: 'ready',
            hasPendingChanges: true,
            isPreviewing: true,
            error: '',
        });
    }

    /**
     * 主图恢复打开面板时的状态，普通图片仅取消当前预览
     *
     * @returns 重置和宿主回滚完成后的异步结果
     * 副作用：清理预览并回滚宿主临时事务；主图已应用的滤镜恢复为入场快照并写入历史。
     */
    async reset() {
        const session = this.session;
        if (!session || this.getSnapshot().status === 'applying') return;
        if (session.originLayer.layerType !== LayerType.MAIN_LAYER) {
            await this.cancel({ silent: true });
            return;
        }

        const revision = ++session.revision;
        session.resultCanvas = null;
        this.alphaResultMap.delete(session.layerId);
        this.setSnapshot({ status: 'applying', error: '' });
        try {
            if (session.host.rollback) await session.host.rollback();
            if (this.session !== session || session.revision !== revision) return;

            const oldLayer = this.framework.service.flatLayer.getLayer(session.layerId, true);
            // 退出合并模式后临时主图已移除，由宿主为恢复后的主图重新打开会话。
            if (!oldLayer) {
                this.destroy();
                return;
            }
            const newLayer = cloneDeep(session.originLayer);
            if ((oldLayer.data as IImageLayerProps).url !== (newLayer.data as IImageLayerProps).url) {
                this.framework.service.frame.addOrUpdateLayer(newLayer);
                this.framework.service.history.write({
                    type: IMAGE_FILTER_HISTORY_TYPE,
                    data: { item: { oldLayer: cloneDeep(oldLayer), newLayer: cloneDeep(newLayer) } },
                    originIndex: oldLayer.__originIndex,
                });
            }
            session.sourceCanvas = session.originCanvas;
            this.clearPreview(session.layerId, session.sourceCanvas);
            this.resetState(session.layerId);
        } catch (error) {
            if (this.session === session) this.emitError(error);
            throw error;
        }
    }

    /**
     * 应用当前滤镜结果
     *
     * @param options - 可选历史统计标识
     * @returns 成功提交的前后图层及滤镜信息；无草稿时返回 undefined
     * 副作用：仅此方法生成一次 base64，并更新正式图层或调用宿主提交事务。
     */
    async apply(options: { trackFunc?: string } = {}): Promise<IImageFilterApplyResult | undefined> {
        const session = this.session;
        const snapshot = this.getSnapshot();
        const alphaResult = session ? this.alphaResultMap.get(session.layerId) : undefined;
        if (snapshot.status === 'applying') return;
        if (!session || !snapshot.filter || !snapshot.hasPendingChanges || !alphaResult) return;
        if (alphaResult.revision !== session.revision) return;

        this.setSnapshot({ status: 'applying', error: '' });
        try {
            const oldLayer = this.framework.service.flatLayer.getLayer(session.layerId, true);
            if (!oldLayer) throw new Error(`Image filter target is missing: ${session.layerId}`);
            const base64 = alphaResult.canvas.toDataURL('image/png');
            const newLayer = cloneDeep(oldLayer);
            const newLayerData = newLayer.data as IImageLayerProps;
            newLayerData.url = base64Kit.getCode(base64, uuid());
            // 图片本体已经变化，旧特效截图的透明度轮廓不再可信；正式渲染会按新 URL 重新生成派生产物。
            if (newLayerData.effect) {
                newLayerData.effect.screenshotUrl = '';
                delete newLayerData.effect.effectPosition;
            }
            appendAppliedFilterId(newLayer, alphaResult.filter.filterId);

            const context: IImageFilterCommitContext = {
                oldLayer: cloneDeep(oldLayer),
                newLayer,
                originLayer: cloneDeep(session.originLayer),
                trackFunc: options.trackFunc,
            };
            const committedByHost = (await session.host.commit?.(context)) ?? false;
            if (!committedByHost) {
                this.framework.service.frame.addOrUpdateLayer(newLayer);
                this.framework.service.history.write({
                    type: IMAGE_FILTER_HISTORY_TYPE,
                    data: { item: { oldLayer: cloneDeep(oldLayer), newLayer: cloneDeep(newLayer) } },
                    originIndex: oldLayer.__originIndex,
                    trackFunc: options.trackFunc,
                });
            }

            this.getObject(session.layerId)?.commitAppliedResource?.();
            this.clearPreview(session.layerId);
            // 应用只推进后续滤镜的叠加基图，“无滤镜”仍以打开面板时的快照为恢复基准。
            session.sourceCanvas = cloneFilterCanvas(alphaResult.canvas);
            session.resultCanvas = null;
            session.revision += 1;
            this.alphaResultMap.delete(session.layerId);
            const result: IImageFilterApplyResult = {
                oldLayer: cloneDeep(oldLayer),
                newLayer: cloneDeep(newLayer),
                filterId: alphaResult.filter.filterId,
                categoryId: alphaResult.filter.categoryId,
                alpha: snapshot.alpha,
            };
            this.framework.service.events.emit(ImageFilterEvent.APPLIED, result);
            this.setSnapshot({
                filter: null,
                status: 'idle',
                hasPendingChanges: false,
                isPreviewing: false,
                error: '',
            });
            return result;
        } catch (error) {
            this.emitError(error);
            throw error;
        }
    }

    /**
     * 取消当前未提交滤镜草稿
     *
     * @param options - 可选；silent 为 true 时仅回滚预览，不触发取消业务回调
     * @returns 宿主回滚完成后的异步结果
     * 副作用：恢复正式图层画面、清理私有结果并调用可选宿主回滚。
     */
    async cancel(options: { silent?: boolean } = {}) {
        const session = this.session;
        if (!session) return;
        const snapshot = this.getSnapshot();
        if (snapshot.status === 'applying') return;
        const result: IImageFilterCancelResult = {
            layerId: session.layerId,
            filterId: snapshot.filter?.filterId ?? '',
            categoryId: snapshot.filter?.categoryId ?? '',
            alpha: snapshot.alpha,
        };
        session.revision += 1;
        session.resultCanvas = null;
        this.alphaResultMap.delete(session.layerId);
        this.clearPreview(session.layerId, session.sourceCanvas);
        try {
            await session.host.rollback?.();
            if (this.session !== session) return result;
            if (!options.silent) this.framework.service.events.emit(ImageFilterEvent.CANCELLED, result);
            this.setSnapshot({
                filter: null,
                alpha: 1,
                status: 'idle',
                hasPendingChanges: false,
                isPreviewing: false,
                error: '',
            });
            return result;
        } catch (error) {
            if (this.session === session) this.emitError(error);
            throw error;
        }
    }

    /**
     * 取消草稿并原子销毁当前会话
     *
     * @returns 当前会话关闭完成后的异步结果
     * 副作用：存在滤镜草稿时先静默回滚，不触发取消业务回调；异步期间打开的新会话不会被旧清理误销毁。
     */
    async close() {
        const session = this.session;
        if (!session) return;
        const snapshot = this.getSnapshot();
        if (snapshot.status === 'applying') return;
        if (snapshot.filter || snapshot.hasPendingChanges) await this.cancel({ silent: true });
        if (this.session === session) this.destroy();
    }

    /**
     * 销毁当前滤镜会话
     *
     * 副作用：使异步任务失效并清理临时资源；不会调用宿主回滚或隐式提交。
     */
    destroy() {
        this.lifecycleRevision += 1;
        const session = this.session;
        if (session) {
            session.revision += 1;
            this.alphaResultMap.delete(session.layerId);
            this.clearPreview(session.layerId, session.sourceCanvas);
        }
        this.session = null;
        this.resetState();
    }

    /**
     * 释放插件长期监听
     *
     * 副作用：销毁会话并解绑历史监听。
     */
    dispose() {
        this.destroy();
        if (this.historyListenerId) {
            this.framework.service.events.off(this.historyListenerId);
            this.historyListenerId = 0;
        }
    }

    /**
     * 获取当前稳定 UI 快照
     *
     * @returns 插件 Store 中的只读快照引用
     */
    getSnapshot(): IImageFilterUISnapshot {
        return this.store.getState().snapshot;
    }

    /**
     * 订阅 UI 快照变化
     *
     * @param listener - 状态变化监听器
     * @returns 仅解绑本次监听的函数
     */
    subscribe(listener: ImageFilterStateListener) {
        const handler = this.framework.service.events.on(
            ImageFilterEvent.STATE_CHANGE,
            (snapshot: IImageFilterUISnapshot) => listener(snapshot),
        );
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 订阅滤镜应用成功事件
     *
     * @param listener - 应用结果监听器
     * @returns 仅解绑本次监听的函数
     */
    onApplied(listener: (result: IImageFilterApplyResult) => void) {
        const handler = this.framework.service.events.on(ImageFilterEvent.APPLIED, listener);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 订阅滤镜取消事件
     *
     * @param listener - 取消结果监听器
     * @returns 仅解绑本次监听的函数
     */
    onCancelled(listener: (result: IImageFilterCancelResult) => void) {
        const handler = this.framework.service.events.on(ImageFilterEvent.CANCELLED, listener);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 查询当前会话是否存在未提交修改
     *
     * @returns 存在未提交滤镜结果时返回 true
     */
    hasPendingChanges() {
        return this.getSnapshot().hasPendingChanges;
    }

    /**
     * 读取画布当前稳定预览的滤镜素材，供商业化计算使用
     *
     * 新滤镜加载期间仍返回上一张可见预览的素材，不修改正式图层或历史。
     *
     * @param layerId - 待查询的图层 ID
     * @returns 稳定预览的素材 ID；没有预览时返回 undefined
     */
    getPreviewFilterId(layerId: string): string | undefined {
        if (this.session?.layerId !== layerId) return undefined;
        return this.alphaResultMap.get(layerId)?.filter.filterId;
    }

    /**
     * 把当前稳定可见的滤镜结果投影到输出副本
     *
     * loading 期间继续使用最后一个已发布到画布的结果；不会提交正式图层、写历史或改变面板状态。
     *
     * @param layer - 保存或下载链路创建的克隆图层
     * @returns 成功投影稳定滤镜结果时返回 true
     * 副作用：仅更新传入图层及其瞬时输出 Canvas。
     */
    projectOutputDraft(layer: ILayerProps) {
        const session = this.session;
        if (!session || session.layerId !== layer.id) return false;
        const alphaResult = this.alphaResultMap.get(layer.id);
        if (!alphaResult) return false;

        setLayerOutputSourceCanvas(layer, alphaResult.canvas);
        const data = layer.data as IImageLayerProps;
        if (data.effect) {
            data.effect.screenshotUrl = '';
            delete data.effect.effectPosition;
        }
        appendAppliedFilterId(layer, alphaResult.filter.filterId);
        return true;
    }

    /**
     * 查询指定图层是否正在显示滤镜预览
     *
     * @param layerId - 图层 ID
     * @returns 当前会话属于该图层且正在预览时返回 true
     */
    isPreviewing(layerId: string) {
        const snapshot = this.getSnapshot();
        return snapshot.layerId === layerId && snapshot.isPreviewing;
    }

    /**
     * 获取滤镜列表使用的远程封面地址
     *
     * @param layerId - 图层 ID
     * @param fallbackUrl - 透明图片使用的默认封面
     * @returns 封面地址及请求对应图层 ID
     */
    async getCoverUrl(layerId: string, fallbackUrl: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerId);
        if (!layer?.data) return { url: '', layerId };
        const data = layer.data as IImageLayerProps;
        if (data.isTransparent) return { url: fallbackUrl, layerId };
        const remoteUrl = await base64Kit.getRemoteUrlAsync(data.url);
        return { url: convertImageToOrigin(remoteUrl), layerId };
    }
}
